<template>
    <div>
        <div class="loader__wrapper loader__wrapper--loading loader__wrapper--xl" v-if="!study || !studyLoaded">
            <div class="loader">
                <div class="loader__circle"></div>
                <div class="loader__text">{{labels.loadingStudie}}</div>
            </div>
        </div>
        <div class="grid-container" v-if="study && studyLoaded">
            <div class="grid-x">
                <div class="head head--blue cell">
                    <div class="head__content">
                        <a @click="$router.go(-1)" @keyup.enter="$router.go(-1)" tabindex="0" class="head__back">
                            <span class="icon-arrow-left"></span>
                            {{labels.back}}
                        </a>
                        <h1 class="head__title">{{study.title}}</h1>
                        <p class="small">{{study.authors}}</p>
                    </div>
                    <footer class="head__foot">
                        <span class="head__foot-item" style="margin-right:50px">
                            <span>{{labels.placeOfPublication}}</span> 
                            <a tabindex="0"  target="_blank" 
                                :href="'https://scholar.google.dk/scholar?q='+study.title">
                                {{ study.publicationPlace }}
                                <span v-if="study.publicationComments">, {{ study.publicationComments}}</span>    
                            </a>

                            <popup-info-box 
                                :popoverClasses="'popover--icon'" 
                                :text="this.labels.findTheStudyOnGoogleScholar">
                            </popup-info-box>

                        </span>
                        <span class="head__foot-item">
                            <span>{{labels.yearOfPublication}}</span> {{study.publicationYear}}</span>
                    </footer>
                </div>
                <div class="content content--gray content--offset cell">
                    <p class="intro">{{study.examinesWhat}}</p>
                    <div class="grid-x">
                        <div class="medium-8 cell content__text" v-if="study.examinesHow && study.findings && study.challenges">
                            <h2 class="h3">{{labels.howIsItInvestigated}}</h2>
                            <div class="content__rich" v-html="study.examinesHow"></div>
                            <h2 class="h3">{{labels.whatAreTheFindings}}</h2>
                            <div class="content__rich" v-html="study.findings"></div>
                            <h2 class="h3">{{labels.whatAreTheChallenges}}</h2>
                            <div class="content__rich" v-html="study.challenges"></div>
                        </div>
                        <div class="medium-8 cell content__text" v-else>
                            <h2 class="h3">{{labels.oldDescriptionTitle}}</h2>
                            <div class="content__rich" v-html="study.oldDescription" v-if="study.oldDescription"></div>
                            <p class="lead" v-else><i>{{labels.descriptionIsMissing}}</i></p>
                        </div>
                        <div class="medium-4 cell content__factbox">
                            <div class="content__factbox-content grid-y">
                                <div class="content__factbox-item cell">
                                    <h2 class="h3">{{labels.factbox}}</h2>
                                </div>
                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.geography}}</small>
                                    <p><b>{{study.country}}</b></p>
                                </div>
                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.analyticalMethod}}</small>
                                    <p><b>{{study.type}}</b></p>
                                </div>

                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.publishedInAScientificJournal}}</small>
                                    <p><b>{{study.publishedInScientificJournal}}</b></p></div>     
                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.scientificJournal}}</small>
                                    <p><b>{{study.publicationPlace}}</b></p></div> 
                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.quality}}</small>
                                    <p><b>{{study.assessment}}</b></p></div>
                                <div class="content__factbox-item cell">
                                    <small class="text-upper">{{labels.assessedBy}}</small>
                                    <p><b>{{study.evaluatedByUser}}</b></p>
                                </div>    
                            </div>
                        </div>
                    </div>
                </div>
                <div class="cell">
                    <h2 class="h1 text-center accordion__header"><br />{{labels.resultsOfTheStudy}}</h2>
                    <a @click="toggleAll(expandable)" @keyup.enter="toggleAll(expandable)" v-if="study.results.length > 1" class="accordion__toggle" tabindex="0">
                        <template v-if="expandable">
                            <span class="icon-plus"></span>
                            {{labels.unfoldAll}}
                        </template>
                        <template v-else>
                            <span class="icon-minus"></span>
                            {{labels.foldAll}}
                        </template>
                    </a>
                  
                    <ul class="accordion">
                        <li class="accordion__item" 
                            v-for="(result, key, index) of study.results" 
                            :key="index" 
                            :class="{'accordion__item--open': result.open}">
                            <a @click="result.open=!result.open" @keyup.enter="result.open=!result.open" tabindex="0" class="accordion__title accordion--study">
                                <h3 class="accordion__title-truncate">{{result.title || 'Resultat navn mangler'}}</h3>
                                <p class="accordion__text-left-truncate">{{ labels.targetGroup }}: {{ toList(result.targetGroups[0].elements) }}</p>
                                <p class="accordion__text-right-truncate" v-for="(gender, i) in result.gender">{{ labels.genders }}: {{ toList(gender.elements) }}</p>
                            </a>
                            <div class="accordion__head">
                                <span class="accordion__head-item">
                                    <span class="dimm">{{labels.effectSize}}</span>
                                    {{ result.effectSize }}
                                </span>
                                <span class="accordion__head-item">
                                    <span class="dimm">{{labels.effectMeasurement}}</span>
                                    {{result.effectTarget}}
                                </span>
                                <span class="accordion__head-item">
                                    <span class="dimm">{{labels.effectType}}</span>
                                    {{result.effectType}}
                                </span>
                            </div>
                            <div class="accordion__content">
                                <div class="grid-x grid-padding-x">
                                    <div class="large-4 cell">
                                        <p><b>{{labels.studyConclusionOnEffect}}</b></p>
                                            <div class="label label--mega" :class="effectLabels[result.effectConclusion-1].labelClass">
                                                {{result.effectConclusionName}}
                                            </div>
                                        <p>&nbsp;</p>
                                    </div>
                                    <div class="large-8 cell">
                                        <p><b>{{labels.commentFromResearcher}}</b><br>{{result.comment}}</p>
                                    </div>
                                </div>
                                
                                <div class="accordion__facts grid-x grid-padding-x">
                                    <div class="large-3 cell">
                                        <h4 class="h3">{{labels.theInvestigation}}</h4>
                                        <hr>
                                        <small class="text-upper">{{labels.investigationPeriod}}</small> 
                                        <p><b>{{result.startYear}}-{{result.endYear}}</b></p>
                                        <small class="text-upper">{{labels.numberOfObservations}}</small>
                                        <p><b>{{result.observations || labels.notSpecified}}</b></p>
                                        <small class="text-upper">{{labels.numberOfParticipants}}</small> 
                                        <p><b>{{result.participants || labels.notSpecified}}</b></p>
                                    </div>

                                    <div class="large-3 cell">
                                        <h4 class="h3">{{labels.intervention}}</h4>
                                        <hr>
                                        <div v-for="(item, i) in result.activities" :key="i">
                                            <small class="text-upper">{{item.title}}</small> 
                                            <p><b>{{ toList(item.elements) }}</b></p>    
                                        </div>
                                    </div>

                                    <div class="large-3 cell">
                                        <h4 class="h3">{{labels.targetGroup}}</h4>
                                        <hr>
                                        <div v-for="(item, i) in result.targetGroups" :key="i">
                                            <small class="text-upper">{{item.title}}</small> 
                                            <p><b>{{ toList(item.elements) }}</b></p>    
                                        </div>
                                    </div>

                                    <div class="large-3 cell">
                                        <h4 class="h3">{{labels.otherFilters}}</h4>
                                        <hr>
                                        <small v-if="result.gender.length > 0"class="text-upper">{{labels.genders}}</small>
                                        <p><b><span v-for="(gender, i) in result.gender" :key="i">{{toList(gender.elements)}}</span></b></p>
                                        <small v-if="result.ageGroup.length > 0" class="text-upper">{{labels.ageGroups}}</small>
                                        <p><b><span v-for="(group, i) in result.ageGroup" :key="i">{{toList(group.elements)}}</span></b></p>
                                        <small v-if="result.education.length > 0" class="text-upper">{{labels.education}}</small>
                                        <p><b><span v-for="(group, i) in result.education" :key="i">{{toList(group.elements)}}</span></b></p>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <buttons :hideExport="true" :texts="modalLabels()" :saveStatus="saveStatus" @save="save($event)"></buttons>
            </div>
        </div>
    </div>
</template>

<script>
import { each, some } from "lodash";
import PopupInfoBox from '../PopupInfoBox.vue';

export default {
  components: { PopupInfoBox },
  name: "Study",
  data() {
    return {
      loaded: false,
      saveStatus: null,
      effectLabels: [
        {
          labelClass: "label--red"
        },
        {
          labelClass: "label--yellow"
        },
        {
          labelClass: "label--green"
        }
      ]
    };
  },
  computed: {
    expandable() {
      return some(this.study.results, { open: false });
    },
    study() {
      return this.$store.state.study;
    },
    studyLoaded() {
      return this.$store.state.studyLoaded;
    },
    labels() {
      return this.$store.state.labels["pages.studysummary"] || {};
    }
  },
  methods: {
    toggleAll(shouldOpen) {
      each(this.study.results, result => {
        result.open = shouldOpen;
      });
    },
    toList(arr) {
      return typeof arr !== "undefined" && arr.length > 0
        ? arr.join(", ")
        : "-";
    },
    save(msg) {
      this.$store
        .dispatch("saveStudyToMyPage", {
          studyId: this.$route.params.id,
          description: msg.msg
        })
        .then(() => {
          this.saveStatus = "succes";
        })
        .catch(error => {
          this.saveStatus = "error";
          console.warn(error);
        });
    },
    modalLabels() {
      return {
        saveDialog: {
          title: this.labels.saveInMyLibraryTitle,
          text: this.labels.saveInMyLibraryText,
          succes: this.labels.saveInMyLibrarySucces,
          error: this.labels.saveInMyLibraryError,
          button: this.labels.saveInMyLibraryButton,
          note: this.labels.saveInMyLibraryNote
        },
        emailDialog: {
          button: this.labels.tipEmailButton,
          message: this.labels.tipEmailMessage,
          receiverEmail: this.labels.tipEmailRecieverEmail,
          receiverName: this.labels.tipEmailRecieverName,
          senderEmail: this.labels.tipEmailSenderEmail,
          senderName: this.labels.tipEmailSenderName,
          success: this.labels.tipEmailSuccess,
          text: this.labels.tipEmailText,
          title: this.labels.tipEmailTitle
        }
      };
    }
  },
  mounted() {
    this.$store.dispatch("getStudy", this.$route.params.id).then(() => {
      this.loaded = true;
      document.title = this.study.title;
      document.querySelector('meta[name="description"]').setAttribute("content", this.study.examinesWhat);

      if (this.$route.query.hasOwnProperty("result")) {
        each(this.study.results, result => {
          result.open = result.id === this.$route.query.result;
        });
      }
    });
      this.$store.dispatch("getLabels", { label: "pages.studysummary" });
      
    console.log('labels is', this.labels);
  }
};
</script>



// WEBPACK FOOTER //
// src/components/jobeffects-search/Study.vue